import { Controller } from '@hotwired/stimulus'

// Выбор нескольких записей (городов, клубов) с подсказками с сервера.
//
// В отличие от tournament-club, откуда взяты дебаунс, отбрасывание устаревших
// ответов и закрытие по клику вне, здесь нужен не текст, а id: форма тренера
// связывает карточку с городами и клубами, а не пишет их названия строкой.
// Поэтому выбранное живёт чипами со скрытыми input[name="coach[city_ids][]"],
// и без JS поле просто останется пустым — сервер обязательные города всё равно
// проверяет сам.
export default class RecordPickerController extends Controller {
  static targets = ['input', 'dropdown', 'chips', 'template']
  static values = {
    url: String,
    kind: String,
    field: String,
    max: { type: Number, default: 0 },
    // Строка «не нашли город» с почтой: подпись, тема письма и mailto приходят
    // из вёрстки, чтобы переводы остались на сервере. Пусто — строки нет.
    fallbackLabel: String,
    fallbackMailto: String,
    fallbackSubject: String
  }

  connect() {
    this.onDocumentClick = (event) => {
      if (!this.element.contains(event.target)) this.close()
    }
    document.addEventListener('click', this.onDocumentClick)
    // Форма возвращается с сервера вместе с уже выбранными чипами — состояние
    // поля надо привести к ним сразу, а не ждать первого выбора.
    this.syncInputState()
  }

  disconnect() {
    document.removeEventListener('click', this.onDocumentClick)
    clearTimeout(this.timer)
  }

  search() {
    clearTimeout(this.timer)
    const query = this.inputTarget.value.trim()
    if (query.length < 2) {
      // Ответ уже улетевшего запроса не должен открыть подсказки заново над
      // опустевшим полем: fallback-строка взяла бы в тему письма пустой или
      // обрезанный город. Сдвигаем идентификатор — поздний ответ отбросится.
      this.lastRequestId = (this.lastRequestId || 0) + 1
      this.close()
      return
    }
    this.timer = setTimeout(() => this.fetchSuggestions(query), 300)
  }

  async fetchSuggestions(query) {
    // Медленный ответ не должен перерисовать подсказки поверх результатов
    // более позднего ввода.
    const requestId = (this.lastRequestId = (this.lastRequestId || 0) + 1)
    try {
      const url = `${this.urlValue}?kind=${encodeURIComponent(this.kindValue)}&q=${encodeURIComponent(query)}`
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      })
      if (requestId !== this.lastRequestId || !response.ok) return

      const data = await response.json()
      if (requestId !== this.lastRequestId) return
      this.render(data.suggestions || [])
    } catch {
      this.close()
    }
  }

  render(suggestions) {
    const available = suggestions.filter(
      ({ id }) => !this.pickedIds.includes(String(id))
    )
    // Пустой список больше не закрывает подсказки: как раз когда ничего не
    // нашлось, нужна строка «не нашли город» — иначе поле обязательно, а
    // выбрать нечего, и человек уходит ни с чем.
    if (!available.length && !this.hasFallbackMailtoValue) {
      this.close()
      return
    }

    this.dropdownTarget.innerHTML = ''
    available.forEach(({ id, label }) => {
      const item = document.createElement('button')
      item.type = 'button'
      item.className = this.#itemClassName
      item.textContent = label
      item.addEventListener('click', () => this.pick(id, label))
      this.dropdownTarget.appendChild(item)
    })

    if (this.hasFallbackMailtoValue) {
      this.dropdownTarget.appendChild(this.#fallbackItem())
    }

    this.dropdownTarget.classList.remove('hidden')
  }

  // Тот же вид, что у подсказок клуба в виджет-ките страницы «Клубам».
  get #itemClassName() {
    return 'block w-full px-4 py-3 text-left text-r-14 text-text-primary hover:bg-btn-gray-overlay-low'
  }

  // Обычное письмо, без формы и заявки в базе: город в базу всё равно заводим
  // руками. Напечатанное подставляем в тему, чтобы человек не набирал название
  // второй раз.
  #fallbackItem() {
    const name = this.inputTarget.value.trim()
    const item = document.createElement('a')
    item.className = `${this.#itemClassName} border-t-1 border-divider text-text-blue`
    item.textContent = this.fallbackLabelValue
    item.href = this.hasFallbackSubjectValue
      ? `${this.fallbackMailtoValue}?subject=${encodeURIComponent(
          this.fallbackSubjectValue.replace('%{city}', name)
        )}`
      : this.fallbackMailtoValue

    return item
  }

  pick(id, label) {
    if (this.atLimit) return

    // Разметка чипа приходит из <template> в форме: там же ей пользуются уже
    // выбранные записи, и классы не расходятся между сервером и скриптом.
    const chip = this.templateTarget.content.firstElementChild.cloneNode(true)
    chip.dataset.recordPickerId = String(id)
    chip.querySelector('span').textContent = label

    const hidden = chip.querySelector('input')
    hidden.name = this.fieldValue
    hidden.value = String(id)

    this.chipsTarget.appendChild(chip)
    this.inputTarget.value = ''
    this.close()
    this.syncInputState()
  }

  remove(event) {
    event.target.closest('[data-record-picker-id]')?.remove()
    this.syncInputState()
  }

  get pickedIds() {
    return Array.from(
      this.chipsTarget.querySelectorAll('[data-record-picker-id]')
    ).map((chip) => chip.dataset.recordPickerId)
  }

  get atLimit() {
    return this.maxValue > 0 && this.pickedIds.length >= this.maxValue
  }

  // На пределе поле ввода блокируем: молча игнорировать выбор хуже, чем
  // показать, что больше нельзя.
  //
  // required снимается, как только выбран хоть один город: значение уезжает
  // скрытыми полями чипов, а сама строка поиска после выбора очищается — и
  // валидация формы ругалась «This field is required» на заполненное поле.
  // Обязательность городов при этом никуда не делась, её проверяет сервер
  // (Account::CoachProfilesController#form_valid?).
  syncInputState() {
    this.inputTarget.disabled = this.atLimit

    if (!this.#requiredWhenEmpty) return

    this.inputTarget.required = this.pickedIds.length === 0
    // Уже нарисованную ошибку (в том числе серверную) снимает только сам
    // input-контроллер — просим его перепровериться. Если ошибки нет, ничего
    // не трогаем: иначе поле краснело бы прямо при открытии формы.
    if (this.element.querySelector('.is-invalid')) {
      this.inputTarget.dispatchEvent(new Event('blur'))
    }
  }

  // Требование записано в разметке один раз; после первого снятия атрибута
  // читать его же было бы нельзя.
  get #requiredWhenEmpty() {
    if (typeof this.hasRequiredFlag === 'undefined') {
      this.hasRequiredFlag = this.inputTarget.required
    }
    return this.hasRequiredFlag
  }

  close() {
    this.dropdownTarget.classList.add('hidden')
    this.dropdownTarget.innerHTML = ''
  }
}
